import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ArrowUpDown, Plus, Search, Upload, Users } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

function SortableHead({ field, current, order, onSort, children, className = "" }) {
  const active = current === field;
  const Icon = !active ? ArrowUpDown : order === "asc" ? ArrowUp : ArrowDown;
  return <TableHead className={className}>
    <button type="button" className="flex items-center gap-1.5 whitespace-nowrap hover:text-foreground" onClick={() => onSort(field)}>
      {children}<Icon className="h-3.5 w-3.5" />
    </button>
  </TableHead>;
}

export default function Customers() {
  const { t } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [status, setStatus] = useState("true");
  const [sort, setSort] = useState("name");
  const [order, setOrder] = useState("asc");

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["customers", q, page, pageSize, status, sort, order],
    queryFn: async () => (await api.get("/customers", { params: {
      q, page, page_size: pageSize, sort, order, is_active: status === "all" ? "" : status,
    } })).data,
    placeholderData: keepPreviousData,
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;
  const changeSort = (field) => {
    if (field === sort) setOrder((value) => value === "asc" ? "desc" : "asc");
    else { setSort(field); setOrder("asc"); }
    setPage(1);
  };

  return (
    <div>
      <PageHeader
        title={t("customers.title")}
        subtitle={t("customers.subtitle")}
        actions={can("customers:write") && (<>
          <Button variant="outline" onClick={() => navigate("/data-import/customers")} data-testid="import-customers-button">
            <Upload className="h-4 w-4" />{t("data_import.import")}
          </Button>
          <Button onClick={() => navigate("/customers/new")} data-testid="new-customer-button">
            <Plus className="h-4 w-4" />{t("customers.new")}
          </Button>
        </>)}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder={t("common.search")}
            className="pl-9"
            data-testid="customer-search-input"
          />
        </div>
        <Select value={status} onValueChange={(value) => { setStatus(value); setPage(1); }}>
          <SelectTrigger className="w-40" aria-label={t("common.status")}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="true">{t("common.active")}</SelectItem>
            <SelectItem value="false">{t("common.inactive")}</SelectItem>
            <SelectItem value="all">{t("common.all")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={String(pageSize)} onValueChange={(value) => { setPageSize(Number(value)); setPage(1); }}>
          <SelectTrigger className="w-28" aria-label="Einträge pro Seite"><SelectValue /></SelectTrigger>
          <SelectContent>{[25, 50, 100].map((size) => <SelectItem key={size} value={String(size)}>{size} / Seite</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Card className="overflow-hidden">
        {isLoading ? (
          <div className="space-y-2 p-4">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : data.items.length === 0 ? (
          <EmptyState
            icon={Users}
            title={t("customers.empty")}
            testId="customers-empty"
            action={can("customers:write") && (
              <Button onClick={() => navigate("/customers/new")}><Plus className="h-4 w-4" />{t("customers.new")}</Button>
            )}
          />
        ) : (
          <Table containerClassName="max-h-[65vh]" data-testid="customers-table">
            <TableHeader className="[&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-background">
              <TableRow>
                <SortableHead field="customer_number" current={sort} order={order} onSort={changeSort}>{t("customers.number")}</SortableHead>
                <SortableHead field="name" current={sort} order={order} onSort={changeSort}>{t("common.name")}</SortableHead>
                <SortableHead field="email" current={sort} order={order} onSort={changeSort} className="hidden md:table-cell">{t("common.email")}</SortableHead>
                <SortableHead field="city" current={sort} order={order} onSort={changeSort} className="hidden lg:table-cell">{t("customers.city")}</SortableHead>
                <SortableHead field="is_active" current={sort} order={order} onSort={changeSort}>{t("common.status")}</SortableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((c) => (
                <TableRow
                  key={c.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/customers/${c.id}`)}
                  data-testid={`customer-row-${c.id}`}
                >
                  <TableCell className="font-medium tabular-nums">{c.customer_number}</TableCell>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="hidden text-slate-500 md:table-cell">{c.email || "—"}</TableCell>
                  <TableCell className="hidden text-slate-500 lg:table-cell">{c.billing_city || "—"}</TableCell>
                  <TableCell><StatusBadge status={c.is_active ? "active" : "inactive"} labelKey={c.is_active ? "common.active" : "common.inactive"} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {data && data.total > pageSize && (
        <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
          <span>{t("common.total")}: {data.total}</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1 || isFetching} onClick={() => setPage((p) => p - 1)} data-testid="customers-prev">{t("common.previous")}</Button>
            <span className="tabular-nums">{page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages || isFetching} onClick={() => setPage((p) => p + 1)} data-testid="customers-next">{t("common.next")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
